import { Alert, Box, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Callout from 'modules/core/components/Callout'
import Select from 'modules/core/components/Inputs/Select'
import i18n from 'modules/core/lib/i18n'

import TooltipIcon from 'modules/shift_management/components/ShiftManagement//TooltipIcon'
import AssigneesSelector from 'modules/shift_management/components/ShiftManagement/AssigneesSelector'
import RangeDatePicker from 'modules/shift_management/components/ShiftManagement/RangeDatePicker'
import ShiftLocationPicker from 'modules/shift_management/components/ShiftManagement/ShiftLocationPicker'
import ShiftNotesField from 'modules/shift_management/components/ShiftManagement/ShiftNotesField'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type ApplyShiftTemplateForm from 'modules/shift_management/lib/forms/ApplyShiftTemplateForm'
import type ShiftTemplate from 'modules/shift_management/store/api/models/ShiftTemplate'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'

import TemplatePicker from './TemplatePicker'

type IdsTypes = number[] | (number | null | undefined)

export interface Props {
  form: ApplyShiftTemplateForm
  shiftTemplates: ShiftTemplate[]
  selectableEmployeeIds: number[]
  weeksByTemplate: () => TemplateWeek[]
  mixedValuesByLocation: boolean
  mixedValuesByWorkArea: boolean
}

const FormContent = ({
  form,
  shiftTemplates,
  selectableEmployeeIds,
  weeksByTemplate,
  mixedValuesByLocation,
  mixedValuesByWorkArea,
}: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const locationsEnabled = hasFeatureFlag('dev_location_work_areas')
  const templateErrors = form.get('template_id')?.errors ?? []
  const showTemplateError = !!templateErrors.length

  const weekOptions = weeksByTemplate().map((week) => ({
    value: week.attributes.position,
    label: week.attributes.name,
  }))

  const showWeeks = weekOptions.length > 1
  const hasZeroWeeksWarning = !!form.get('template_id').value && !weekOptions.length

  const onChangeTemplate = () => {
    form.get('template_week_position').set(0)
    form.get('template_id').setErrors([])
  }

  return (
    <Box gap="s16">
      <AssigneesSelector form={form} selectableEmployeeIds={selectableEmployeeIds} />

      <TemplatePicker
        field={form.get('template_id')}
        shiftTemplates={shiftTemplates}
        onChange={onChangeTemplate}
      />
      {showTemplateError && (
        <Box className="error-anchor">
          <Callout
            type="critical"
            title={i18n.t('shift_management.actions_sidepanel.errors.no_rotating_shift.title')}
            description={i18n.t(
              'shift_management.actions_sidepanel.errors.no_rotating_shift.description'
            )}
          />
        </Box>
      )}
      {hasZeroWeeksWarning && (
        <Alert color="warning">
          <Text.Normal>
            {i18n.t('shift_management.rotating_shifts.selected_rotating_shifts_has_zero_weeks')}
          </Text.Normal>
        </Alert>
      )}
      {showWeeks && (
        <Box flexDirection="row" gap="s16" justifyContent="center" alignItems="center" width="full">
          <TooltipIcon.Medium
            icon="calendar"
            label={i18n.t('shift_management.actions_sidepanel.tooltip.starting_period')}
            color="grey700"
          />
          <Select
            field={form.get('template_week_position')}
            options={weekOptions}
            showOriginalLabelText
          />
        </Box>
      )}
      <Box flexDirection="row" gap="s16" justifyContent="center" alignItems="center" width="full">
        <TooltipIcon.Medium
          icon="clock"
          label={i18n.t('shift_management.actions_sidepanel.tooltip.time_period')}
          color="grey700"
        />
        <RangeDatePicker
          startDate={form.get('start_date')}
          endDate={form.get('end_date')}
          hideLabel
        />
      </Box>
      {locationsEnabled && (
        <>
          <ShiftLocationPicker
            locationField={form.get('location_id')}
            workAreaField={form.get('work_area_id')}
            mixedValuesLocation={mixedValuesByLocation}
            mixedValuesWorkArea={mixedValuesByWorkArea}
            newVersion
          />
          <ShiftNotesField notesField={form.get('notes')} newVersion />
        </>
      )}
    </Box>
  )
}

export default observer(FormContent)
